import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangleIcon, CheckCircle2Icon, ExternalLinkIcon, InboxIcon, RefreshCwIcon } from 'lucide-react';
import { Drawer } from '../ui/Drawer';
import { recordSets, type RecordItem, type Urgency } from '../../data/perfectDay';
interface RecordDrawerProps {
  recordSetId: string | null;
  onClose: () => void;
}
type LoadState = 'loading' | 'ready' | 'error';
const urgencyStyles: Record<Urgency, string> = {
  urgent: 'bg-danger-50 text-danger-700 ring-danger-200',
  attention: 'bg-warn-50 text-warn-700 ring-warn-200',
  normal: 'bg-navy-50 text-navy-600 ring-navy-200'
};
export function RecordDrawer({
  recordSetId,
  onClose
}: RecordDrawerProps) {
  const [state, setState] = useState<LoadState>('loading');
  const attemptsRef = useRef<Record<string, number>>({});
  const recordSet = recordSetId ? recordSets[recordSetId] : undefined;
  const load = useCallback(() => {
    if (!recordSetId) return;
    setState('loading');
    const attempt = (attemptsRef.current[recordSetId] ?? 0) + 1;
    attemptsRef.current[recordSetId] = attempt;
    const shouldFail = Boolean(recordSets[recordSetId]?.failFirst) && attempt === 1;
    const timer = window.setTimeout(() => {
      setState(shouldFail ? 'error' : 'ready');
    }, 650);
    return () => window.clearTimeout(timer);
  }, [recordSetId]);
  useEffect(() => {
    if (!recordSetId) return;
    const cleanup = load();
    return cleanup;
  }, [recordSetId, load]);
  if (!recordSet) return null;
  const records = recordSet.records;
  return <Drawer open={Boolean(recordSetId)} onClose={onClose} eyebrow="Records" title={recordSet.title} subtitle={recordSet.subtitle} footer={<div className="flex flex-col gap-2 sm:flex-row sm:justify-between" data-id="element-58" data-mp="39se8xrk4hgiq">
          <button type="button" onClick={onClose} className="inline-flex h-11 items-center justify-center rounded-lg border border-navy-200 px-4 text-sm font-semibold text-navy-700 hover:bg-navy-50" data-id="element-59" data-mp="1kdznbg4ei9rg">
            Close
          </button>
          <button type="button" className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-navy-800 px-4 text-sm font-semibold text-white hover:bg-navy-900 disabled:opacity-50" disabled={state !== 'ready' || records.length === 0} data-id="element-60" data-mp="3keo4biko71en">
            Open full list
            <ExternalLinkIcon className="h-4 w-4" aria-hidden="true" data-id="element-61" data-mp="28rwd7yv8k1d" />
          </button>
        </div>} data-id="element-57" data-mp="30tapgmh1mz3p">
      <div aria-live="polite" aria-busy={state === 'loading'} data-id="element-62" data-mp="7737dm0wd0l6">
        {state === 'loading' ? <ul className="space-y-3" aria-label="Loading records" data-id="element-63" data-mp="bzw9s38w7wu3">
            {[0, 1, 2, 3].map(index => <li key={index} className="rounded-xl border border-navy-100 p-4" data-id="element-64" data-mp="gxo4t64u3z2i">
                <div className="h-4 w-2/5 animate-pulse rounded bg-navy-100" data-id="element-65" data-mp="pz5hsfmy3kmz" />
                <div className="mt-3 h-3 w-4/5 animate-pulse rounded bg-navy-50" data-id="element-66" data-mp="px2nvkio97cz" />
                <div className="mt-2 h-3 w-3/5 animate-pulse rounded bg-navy-50" data-id="element-67" data-mp="2aihkl13iv5i0" />
              </li>)}
          </ul> : null}

        {state === 'error' ? <div className="rounded-xl border border-danger-200 bg-danger-50 p-5 text-center" data-id="element-68" data-mp="21hb0ki1nzck0">
            <AlertTriangleIcon className="mx-auto h-6 w-6 text-danger-600" aria-hidden="true" data-id="element-69" data-mp="1wigkhnv3023l" />
            <h3 className="mt-3 text-sm font-semibold text-danger-700" data-id="element-70" data-mp="2t9s7z8a1h055">
              We couldn’t load these records
            </h3>
            <p className="mt-1 text-sm text-danger-700/80" data-id="element-71" data-mp="2kf42bh0jxgft">
              The connection to Salesforce timed out. Your data is safe — try
              again.
            </p>
            <button type="button" onClick={() => load()} className="mt-4 inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-danger-600 px-4 text-sm font-semibold text-white hover:bg-danger-700" data-id="element-72" data-mp="2fege133pjdpa">
              <RefreshCwIcon className="h-4 w-4" aria-hidden="true" data-id="element-73" data-mp="2rnw1yvgas0cj" />
              Retry
            </button>
          </div> : null}

        {state === 'ready' && records.length === 0 ? <div className="rounded-xl border border-navy-100 bg-navy-50/60 p-6 text-center" data-id="element-74" data-mp="2iqbz5i2sdvzt">
            <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-white ring-1 ring-navy-100" data-id="element-75" data-mp="2nonajx8qcek5">
              <InboxIcon className="h-5 w-5 text-navy-400" aria-hidden="true" data-id="element-76" data-mp="3bfj7t9vyg1ys" />
            </span>
            <h3 className="mt-3 text-sm font-semibold text-navy-800" data-id="element-77" data-mp="3fh4gej1ghu36">
              Nothing left here
            </h3>
            <p className="mt-1 text-sm text-navy-500" data-id="element-78" data-mp="36mq3gn4eevn8">
              Every follow-up in this group is complete. New items appear here
              as they’re assigned to you.
            </p>
          </div> : null}

        {state === 'ready' && records.length > 0 ? <ul className="space-y-3" data-id="element-79" data-mp="2zprav1miqhq">
            {records.map(record => <RecordRow key={record.id} record={record} data-id="element-80" data-mp="cavlrrvt52a6" />)}
          </ul> : null}
      </div>
    </Drawer>;
}
function RecordRow({
  record
}: {
  record: RecordItem;
}) {
  const urgency = record.urgency ?? 'normal';
  const isComplete = record.tag === 'Completed' || record.tag === 'Complete' || record.tag === 'Logged';
  return <li data-id="element-81" data-mp="3piov7f7sk6eb">
      <button type="button" className="flex w-full items-start gap-3 rounded-xl border border-navy-100 bg-white p-4 text-left hover:border-brand-200 hover:bg-brand-50/40" data-id="element-82" data-mp="3pif8q1lyghdd">
        <div className="min-w-0 flex-1" data-id="element-83" data-mp="3c9w7crx4mzw">
          <div className="flex flex-wrap items-center gap-2" data-id="element-84" data-mp="3dbg86qwwjtr">
            <p className="text-sm font-semibold text-navy-900" data-id="element-85" data-mp="3uhs2z7f5oqpz">{record.name}</p>
            <span className="text-xs font-medium text-navy-400" data-id="element-86" data-mp="1eqnem23wka8j">
              {record.id}
            </span>
          </div>
          <p className="mt-1 text-sm text-navy-700" data-id="element-87" data-mp="19slzfs9lkv2c">{record.primary}</p>
          <p className="mt-1 text-xs text-navy-500" data-id="element-88" data-mp="1hss7x2uuzusn">{record.secondary}</p>
        </div>
        <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${isComplete ? 'bg-success-50 text-success-700 ring-success-200' : urgencyStyles[urgency]}`} data-id="element-89" data-mp="1lu3pcl3kx9pr">
          {isComplete ? <CheckCircle2Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-90" data-mp="y4sxxu84croa" /> : null}
          {record.tag}
        </span>
      </button>
    </li>;
}